import anime from "animejs";
import {
  DIMMED_ALPHA,
  DIM_FADE_IN_TENSION,
  DIM_FADE_DURATION_TENSION,
} from "./DimReelSet/DimReelSet";

// A spine under the tension dim is drawn at the brightness the dim leaves a
// texture at: DimReelSet's 0.6 black over it = 0.4 of itself.
const DARK = 1 - DIMMED_ALPHA;
// Blend between the idle and the wait clips, seconds.
const WAIT_MIX = 0.2;
// TEMPORARY (2026-10-05): logs every darkenReel's cells — OFF before the commit.
// const TENSION_DIAG = true;
const TENSION_DIAG = false; // off for the commit (2026-10-06)

// ── The landing gate (Vladimir, 2026-09-30: "if we already triggered it, why
// do next following reels even try to play it") ── while a chain runs, a reel
// that stops AFTER the one that started it never starts its non-trigger
// landing / idle spines — they stay pictures under their dim column, so there
// is nothing to darken or fight. The same rule Zeus's anticipation already
// has in useReelLandingAnimations (`currentRound.anticipation` + not a bonus
// symbol -> no landing), fed from here. Module-level: the landing hook and
// the room's runner share one board.
let landingGate = null;
/**
 * True when the cell must stay a picture (useReelLandingAnimations asks for
 * every landing cell). Records the cell, so a miss can give it its idle.
 */
export const skipTensionLanding = (reelIndex, row, symbolId) =>
  !!landingGate?.(reelIndex, row, symbolId);

/**
 * createTensionSymbols — the symbols of the stopped reels during an
 * anticipation (Vladimir, 2026-09-30: "it must be smooth").
 *
 * No spine is swapped for its texture any more — those swaps raced the
 * landing code (it hides and shows the same cells) and every idle restarted
 * from frame 0 when it came back. Instead:
 *
 *   trigger symbols  (coins, scatters)  play their WAIT clips above the dim —
 *                    c: wait_in -> wait_loop, sc: wait (GDD Anticipation Mode:
 *                    "The two landed Scatters receive a gold glow", "The
 *                    landed Fireball coins emit a stronger orange-gold glow").
 *                    A symbol with no wait clip (cj today) keeps its idle.
 *   every other live spine  darkens in step with its reel's dim column and
 *                    FREEZES at its next loop seam — a landing plays out
 *                    first, dark. Nothing is hidden, nothing restarts.
 *
 * The end: a miss fades the colour back with the dim, the frozen spines
 * resume where they stopped, coins play wait_out -> idle and scatters blend
 * back to idle ("The glow fades out"). A hit hands the dark spines to their
 * textures under the full board dim (the same brightness, so the swap is not
 * seen) and leaves the waits running for the trigger win.
 *
 * @param {object} deps
 * @param {(reelIndex:number) => object} deps.getReelCtrl     ReelControllerV2.
 * @param {(reelIndex:number) => object} deps.getOverlayCtrl  OverlayReelController.
 * @param {(symbolId:number) => boolean} deps.isTrigger        Coin or scatter.
 */
// export const createTensionSymbols = ({ getReelCtrl, getOverlayCtrl, isTrigger }) => {
export const createTensionSymbols = ({
  getReelCtrl,
  getOverlayCtrl,
  isTrigger: isAnyTrigger,
  isCoin,
  isScatter,
  // ── Waits only (Vladimir, 2026-09-30: "revert this reel dimming and
  // animation suppressing ... just make sure that trigger symbols play that
  // wait animation") ── no spine is darkened or frozen, no landing is gated:
  // only the trigger symbols with hope play their wait clips, and a miss
  // plays them out. The tension dim is off (ReelControlContext).
  waitsOnly = false,
}) => {
  // The trigger symbols of THIS chain — only the feature it waits for
  // (arm). Outside a chain, any coin or scatter.
  let isTrigger = isAnyTrigger;
  // spine -> { reelIndex, row, value, tween, listener, frozen }
  const dark = new Map();
  // spine -> { overlayCtrl, symbolId, outName }
  const waiting = new Map();
  // Cells the landing gate kept as pictures: "reel_row" -> { reelIndex, row, symbolId }.
  const skipped = new Map();

  /**
   * The chain has started from `fromReel`: later reels land pictures only.
   * `features` ({ coins, scatters }, anticipationChain) says which symbols can
   * still complete a feature — only they glow; a coin or scatter with no hope
   * is an ordinary symbol for this chain.
   */
  const arm = (fromReel, features = null) => {
    if (features && isCoin && isScatter) {
      isTrigger = (id) =>
        (features.coins && isCoin(id)) || (features.scatters && isScatter(id));
    }
    if (waitsOnly) return;
    landingGate = (reelIndex, row, symbolId) => {
      if (reelIndex <= fromReel || isTrigger(symbolId)) return false;
      skipped.set(`${reelIndex}_${row}`, { reelIndex, row, symbolId });
      return true;
    };
  };
  const disarm = () => {
    landingGate = null;
  };

  // A miss: the pictures the gate kept take their idle — its first frame is
  // the picture, so nothing jumps. Only on a reel that has stopped.
  const idleSkipped = () => {
    for (const { reelIndex, row, symbolId } of skipped.values()) {
      const reelCtrl = getReelCtrl(reelIndex);
      const overlayCtrl = getOverlayCtrl(reelIndex);
      if (!reelCtrl?.hasStopped || !overlayCtrl) continue;
      if (!overlayCtrl.getAvailableAnimations?.(row, symbolId)?.hasIdle) continue;
      overlayCtrl.showIdleAnimation(row, symbolId);
      requestAnimationFrame(() => reelCtrl.hideSymbolTexture?.(row));
    }
    skipped.clear();
  };

  const hasClip = (spine, name) => !!spine?.skeleton?.data?.findAnimation?.(name);

  const setBrightness = (spine, v) => {
    const color = spine?.skeleton?.color;
    if (!color) return;
    color.r = v;
    color.g = v;
    color.b = v;
  };

  const fade = (spine, rec, to, duration) => {
    rec.tween?.pause();
    rec.tween = anime({
      targets: rec.value,
      v: to,
      duration,
      easing: "linear",
      update: () => setBrightness(spine, rec.value.v),
    });
  };

  // ── Darken + freeze at the next loop seam ──
  // A looping clip's `complete` is its seam; a landing's queued idle fires
  // `start` when it takes over — frozen there, the idle's first frame, which
  // is the picture the texture shows.
  const darken = (spine, reelIndex, row) => {
    if (dark.has(spine)) return;
    const rec = {
      reelIndex,
      row,
      value: { v: spine.skeleton?.color?.r ?? 1 },
      tween: null,
      listener: null,
      frozen: false,
    };
    const freeze = (entry) => {
      if (!entry?.loop || rec.frozen) return;
      rec.frozen = true;
      spine.autoUpdate = false;
    };
    rec.listener = { start: freeze, complete: freeze };
    spine.state?.addListener(rec.listener);
    fade(spine, rec, DARK, DIM_FADE_IN_TENSION);
    dark.set(spine, rec);
  };

  // `instant` = a new round: colour back at once, the spine is not touched
  // otherwise (spin start has already hidden it).
  const lift = (spine, instant = false) => {
    const rec = dark.get(spine);
    if (!rec) return;
    dark.delete(spine);
    if (rec.listener) spine.state?.removeListener(rec.listener);
    if (instant) {
      rec.tween?.pause();
      setBrightness(spine, 1);
      return;
    }
    if (rec.frozen && spine.visible) spine.autoUpdate = true;
    fade(spine, rec, 1, DIM_FADE_DURATION_TENSION);
  };

  // ── The wait clips ── after the landing when it is still playing, else
  // blended in from the idle.
  const startWait = (overlayCtrl, entry) => {
    const spine = entry.spineInstance;
    const state = spine?.state;
    if (!state || waiting.has(spine)) return;
    let inName = null;
    let loopName = null;
    if (hasClip(spine, "wait_in") && hasClip(spine, "wait_loop")) {
      inName = "wait_in";
      loopName = "wait_loop";
    } else if (hasClip(spine, "wait")) {
      loopName = "wait";
    } else {
      return;
    }
    const current = state.tracks?.[0];
    if (current && !current.loop && !current.isComplete?.()) {
      state.clearNext(current);
      if (inName) state.addAnimation(0, inName, false, 0);
      state.addAnimation(0, loopName, true, 0);
    } else {
      const first = state.setAnimation(0, inName ?? loopName, !inName);
      if (first) first.mixDuration = WAIT_MIX;
      if (inName) state.addAnimation(0, loopName, true, 0);
    }
    spine.autoUpdate = true;
    waiting.set(spine, {
      overlayCtrl,
      symbolId: entry.symbolId,
      outName: hasClip(spine, "wait_out") ? "wait_out" : null,
    });
  };

  const endWait = (spine, { overlayCtrl, symbolId, outName }) => {
    const state = spine?.state;
    if (!state) return;
    const idle = overlayCtrl?.selectSpineAnimation?.(spine, "idle", symbolId);
    if (outName) {
      const out = state.setAnimation(0, outName, false);
      if (out) out.mixDuration = WAIT_MIX;
      if (idle) state.addAnimation(0, idle, true, 0);
    } else if (idle) {
      const back = state.setAnimation(0, idle, true);
      if (back) back.mixDuration = WAIT_MIX;
    }
  };

  /** A stopped reel under the dim: waits on its triggers, the rest dark. */
  const darkenReel = (reelIndex) => {
    const overlayCtrl = getOverlayCtrl(reelIndex);
    // ── TEMPORARY diagnostic (2026-10-05): why the waits do not play ──
    if (TENSION_DIAG) {
      const rows = overlayCtrl?.activeAnimations
        ? [...overlayCtrl.activeAnimations].map(([row, e]) => ({
            row,
            symbolId: e?.symbolId,
            hasSpine: !!e?.spineInstance,
            visible: e?.spineInstance?.visible,
            alpha: e?.spineInstance?.alpha,
            trigger: isTrigger(e?.symbolId),
            clip: e?.spineInstance?.state?.tracks?.[0]?.animation?.name,
            wait: hasClip(e?.spineInstance, "wait_in") ? "wait_in" : hasClip(e?.spineInstance, "wait") ? "wait" : null,
          }))
        : "no overlay / no activeAnimations";
      // eslint-disable-next-line no-console
      console.info("[TENSION] darkenReel", reelIndex, rows);
    }
    if (!overlayCtrl?.activeAnimations) return;
    for (const [row, entry] of overlayCtrl.activeAnimations) {
      const spine = entry?.spineInstance;
      if (!spine || !spine.visible || spine.alpha < 0.5) continue;
      if (isTrigger(entry.symbolId)) startWait(overlayCtrl, entry);
      // else darken(spine, reelIndex, row);
      else if (!waitsOnly) darken(spine, reelIndex, row);
    }
  };

  /** The chain is over: `hit` = the feature triggers. */
  const finish = (hit) => {
    if (hit) {
      // The gate stays armed to the next round: the reels still to land stay
      // pictures under the board dim, as the ones before them (reset disarms).
      skipped.clear();
      waiting.clear();
      for (const [spine, rec] of [...dark]) {
        const overlayCtrl = getOverlayCtrl(rec.reelIndex);
        if (overlayCtrl?.activeAnimations?.get(rec.row)?.spineInstance === spine) {
          overlayCtrl.hideWinAnimation?.(rec.row);
          getReelCtrl(rec.reelIndex)?.showSymbolTexture?.(rec.row);
        }
        lift(spine, true);
      }
      return;
    }
    for (const [spine, rec] of [...waiting]) endWait(spine, rec);
    waiting.clear();
    for (const spine of [...dark.keys()]) lift(spine);
    // The reels still to land land normally; the kept pictures get idles.
    disarm();
    idleSkipped();
  };

  /** A new round: nothing carries over. */
  const reset = () => {
    isTrigger = isAnyTrigger;
    disarm();
    skipped.clear();
    waiting.clear();
    for (const spine of [...dark.keys()]) lift(spine, true);
  };

  return { darkenReel, finish, reset, arm };
};
